import { Message } from '@arco-design/web-react';
import axios from 'axios';
import notify from './toastService';

export function loadScript(url) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

export const HTTP_STATUS = {
  SUCCESS: 200,
  UNAUTHORIZED: 401,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  CORS_ERROR: 503,
  BAD_GATEWAY: 502,
};

export function handleApiError(status, message, status_code, showAlert) {
  switch (status_code) {
    case HTTP_STATUS.UNAUTHORIZED:
      notify.error(message);
      break;
    case HTTP_STATUS.BAD_REQUEST:
      showAlert(message || 'Something went wrong', {
        enableWarningIcon: true,
      });
      break;
    case HTTP_STATUS.SERVER_ERROR:
      showAlert(message || 'Internal server error', {
        enableWarningIcon: true,
      });
    case HTTP_STATUS.NOT_FOUND:
      showAlert(message || 'Something went wrong', {
        enableWarningIcon: true,
      });
      break;
    case HTTP_STATUS.BAD_GATEWAY:
      showAlert(
        message ||
          'Bad Gateway: The server encountered an error while processing your request. Please try again later.',
        {
          enableWarningIcon: true,
        }
      );
      break;
    case HTTP_STATUS.CORS_ERROR:
      showAlert(
        message ||
          "CORS Error: The server is blocking requests from your application. Please check the server's CORS settings or configuration.",
        {
          enableWarningIcon: true,
        }
      );
      break;
    default:
      showAlert(message || 'Something went wrong', {
        enableWarningIcon: true,
      });
  }
}

export const clearSessionStorage = () => {
  try {
    return localStorage.clear();
  } catch (err) {
    return err;
  }
};

export const resetNavigation = (path) => window.location.replace(path);

export const getSessionStorage = (key, defaultValue) => {
  const storedValue = sessionStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : defaultValue;
};

export const setSessionStorage = (key, value) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const setLocalStorage = (key, value) => {
  try {
    if (typeof value !== 'boolean' && !value) return;
    const valueToStore =
      typeof value === 'object' ? JSON.stringify(value) : value;
    localStorage.setItem(key, valueToStore);
  } catch (error) {
    console.error(`Error setting item to localStorage: ${key}`, error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      try {
        return JSON.parse(storedValue);
      } catch (e) {
        return storedValue;
      }
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error getting item from localStorage: ${key}`, error);
    return null;
  }
};

export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing item from localStorage: ${key}`, error);
  }
};

export const clearLocalStorage = () => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage', error);
  }
};

export const removeLeadingSpace = (value) => {
  if (typeof value !== 'string') {
    value = String(value); // Convert non-string values to string
  }
  return value.startsWith(' ') ? value.slice(1) : value;
};

export const cleanAlphabetsOnly = (value) => {
  return value.replace(/[^a-zA-Z\s]/g, ''); // Allow only alphabetic characters and spaces
};

export const sanitizeEmail = (value) => {
  return value.replace(/[^a-zA-Z0-9@._%-+]/g, ''); // Allow valid email characters
};

export const allowAlphanumericAndSpecialChars = (value) => {
  return value.replace(/[^a-zA-Z0-9\s.,&-]/g, ''); // Allow letters, numbers, spaces, and common special characters
};

export const sanitizePhoneNumber = (value) => {
  return value.replace(/[^0-9]/g, ''); // Allow only digits
};

export const sanitizeZipPostalCode = (value) => {
  return value.replace(/[^a-zA-Z0-9\s-]/g, ''); // Allow alphanumeric characters, spaces, and hyphens
};

export const allowAlphanumeric = (value) => {
  return value.replace(/[^a-zA-Z0-9\s.,&-]/g, ''); // Allow letters, numbers, spaces, and common special characters
};

export const removeSpaces = (value) => {
  return value.replace(/\s/g, ''); // Remove all spaces
};

export const validateAlphabetsSpacesAndSpecialCharsNoNumbers = (value) => {
  return value.replace(/[^A-Za-z\s\W]/g, ''); // This removes any number or character that isn't an alphabet, space, or special character
};

export const dynamicLengthValidator = (minLength, label) => {
  return (value, callback) => {
    if (value && value.length < minLength) {
      let message;

      if (label === 'Mobile Number') {
        message = `${label} must be at least ${minLength} numbers`;
      } else {
        message = `${label} must be at least ${minLength} characters`;
      }
      return callback(message);
    }

    callback(null);
  };
};

export function getAllUrlParams(replace = false) {
  const urlParams = new URLSearchParams(window.location.search);
  const paramsObject = {};

  urlParams.forEach((value, key) => {
    paramsObject[key] = value;
  });

  if (replace) {
    const newUrl = `${window.location.origin}${window.location.pathname}`;
    window.history.replaceState({}, document.title, newUrl);
  }
  return Object.keys(paramsObject).length ? paramsObject : null;
}

export const TRY_MODE = {
  SINGLE: 'single',
  DUAL: 'dual',
};

export const CONTENT_TYPE = {
  PDF: 'application/pdf',
};

export const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof Blob)) {
      reject(new Error('Invalid file type: must be a Blob or File'));
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Generates a unique ID based on the current timestamp and a random two-digit number.
 * @returns {string} A unique ID.
 */
export const generateUniqueId = () => {
  const timestamp = Date.now();
  const randomTwoDigit = Math.floor(Math.random() * 100)
    .toString()
    .padStart(2, '0');
  return `${timestamp}${randomTwoDigit}`;
};

const productCardGradients = [
  'linear-gradient(135deg, #A69BFF 0%, #554AB5 100%)',
  'linear-gradient(135deg, #FFAD80 0%, #BB683B 100%)',
  'linear-gradient(135deg, #C882FB 0%, #7C48A0 100%)',
  'linear-gradient(135deg, #8ECAD5 0%, #3E8694 100%)',
  'linear-gradient(135deg, #649FED 0%, #2F62A6 100%)',
];

export default productCardGradients;

export const productCardRadialGradients = [
  'radial-gradient(120.91% 120.91% at -20.91% -19.09%, #44479F 0%, #131212 100%)',
  'radial-gradient(120.91% 120.91% at -20.91% -19.09%, #9E5034 0%, #131212 100%)',
  'radial-gradient(120.91% 120.91% at -20.91% -19.09%, #85449F 0%, #131212 100%)',
  'radial-gradient(120.91% 120.91% at -20.91% -19.09%, #44799F 0%, #131212 100%)',
];

export const productCardBoxShadow = [
  '-1px 0px 12px 1px #9C9CF85E',
  '-1px 0px 12px 1px #9E6D345E',
  '-1px 0px 12px 1px #C9A5FF5E',
  '-1px 0px 12px 1px #A5CEFF5E',
];

// base64 trim function
export function truncateLongStrings(obj, maxLength = 5000) {
  const cloned = structuredClone(obj);

  const truncate = (str) => {
    const start = str.slice(0, 100);
    const end = str.slice(-50);
    return `${start}...${end}`;
  };

  const traverse = (node) => {
    for (const key in node) {
      if (typeof node[key] === 'string' && node[key].length > maxLength) {
        node[key] = truncate(node[key]);
      } else if (typeof node[key] === 'object' && node[key] !== null) {
        traverse(node[key]);
      }
    }
  };

  traverse(cloned);
  return cloned;
}

export const getInitials = (name) => {
  if (!name) return '';
  const words = name.trim().split(/\s+/); // split by space
  return words
    .slice(0, 2) // take first two words only
    .map((word) => word[0].toUpperCase()) // take first letter and capitalize
    .join('');
};

/**
 * Format key names for display (convert snake_case and camelCase to Title Case)
 * @param {string} key - The key to format
 * @returns {string} - Formatted key
 */
export const formatKey = (key) => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Format tab labels (handles special cases like 'fullResponse')
 * @param {string} key - The tab key to format
 * @returns {string} - Formatted tab label
 */
export const formatTabLabel = (key) => {
  if (key === 'JSON') return 'JSON';
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

/**
 * Format values for display
 * @param {*} value - The value to format
 * @returns {string} - Formatted value
 */
export const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value === '' || (typeof value === 'string' && value.trim() === ''))
    return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Extract UTM parameters from URL
 * @returns {Object} - Object containing UTM parameters
 */
export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    // utm_medium: params.get('utm_medium'),
    // utm_campaign: params.get('utm_campaign'),
    // utm_page: params.get('utm_page'),
    source_page: params.get('source_page'),
  };
};
